<template>
    <div>
        <ChoiceControl v-if="hasIcons()" :value="selectedValues" @input="selectedValues = $event" :options="selectOptions" :multi-choice="true" :icons="icons"/>
        <v-select v-else :options="selectOptions" v-model="selectedValues" :close-on-select="false" multiple/>

        <div class="mb-3 mt-4" v-if="isCustomValueOptionSelected()">
            <label class="w-100 form-label">
                {{ $t('form.labels.customEnumValue') }}
                <input class="form-control"
                       :class="{'is-invalid': isInvalid()}"
                       type="text"
                       v-model="customValue"
                       :placeholder="$t(`parameters.${parameterId}.customValuePlaceholder`)">
                <span class="invalid-feedback d-block" v-if="isInvalid() && customValue.trim() === ''">
                    {{ $t('form.errors.customEnumValueIsBlank') }}
                </span>
            </label>
        </div>
    </div>
</template>

<script lang="ts">
import {Control} from '@/composables/Control';
import {enumIconName, iconsForEstateType} from '@/icons';
import {EstimateEstateType} from '@/model/api';
import {isString, sortBy} from 'lodash-es';
import {computed, defineComponent, inject, PropType, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import vSelect from 'vue-select';
import ChoiceControl from './ChoiceControl.vue';
import {ChoiceOption, EnumControlValue, EnumOption} from './values';

export default defineComponent({
    components: {ChoiceControl, vSelect},
    props: {
        value: {
            type: Array as PropType<EnumControlValue[] | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        options: {
            type: Array as PropType<EnumOption[]>,
            required: true,
        },
        parameterId: {
            type: String,
            required: true,
        },
        customValueOptions: {
            type: Array as PropType<EnumControlValue[]>,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const {t} = useI18n();
        const estateType = inject<EstimateEstateType>('estateType')!;

        const selectedValues = ref<ChoiceOption[]>([]);
        const customValue = ref('');

        const icons = iconsForEstateType(estateType);

        const selectOptions = computed<ChoiceOption[]>(() => {
            return props.options.map(option => ({
                icon: enumIconName(props.parameterId, option),
                value: option,
                label: t(`parameters.${props.parameterId}.options.${option}`),
            }));
        });

        function isCustomValueOptionSelected(): boolean {
            return selectedValues.value.some(option => props.customValueOptions.includes(option.value));
        }

        function getValue(): EnumControlValue[] | null {
            if (selectedValues.value.length === 0) {
                return null;
            }

            return sortBy(selectedValues.value, option => option.value)
                .map(option =>
                    props.customValueOptions.includes(option.value)
                        ? {customValue: customValue.value.trim(), option: option.value}
                        : option.value,
                );
        }

        function validateCurrentValue(): boolean {
            const value = getValue();

            return value === null || (!isCustomValueOptionSelected() || customValue.value.trim() !== '');
        }

        const {updateValue, isInvalid} = Control<EnumControlValue[]>(props, emit, {
            getValue,
            validateCurrentValue,
        });

        watch(selectedValues, () => {
            updateValue();
        });

        watch(customValue, () => {
            updateValue();
        });

        // created logic
        if (props.value !== null) {
            const selectedOptions = props.value.map(item => isString(item) ? item : item.option);

            for (const item of props.value) {
                if (!isString(item)) {
                    customValue.value = item.customValue;
                    break;
                }
            }

            selectedValues.value = selectOptions.value.filter(option => selectedOptions.includes(option.value));
        }

        function hasIcons(): boolean {
            return selectOptions.value.some(option => option.icon in icons);
        }

        return {
            selectedValues,
            customValue,
            selectOptions,
            icons,
            isInvalid,
            hasIcons,
            isCustomValueOptionSelected,
        };
    },
});
</script>
